import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import './markdown-styles.css';
import 'katex/dist/katex.min.css';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface MarkdownRendererProps {
    content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
    // Define custom components for markdown rendering
    const components = {
        // @ts-ignore - Ignoring type issues with ReactMarkdown components
        code({ node, inline, className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match && match[1] ? match[1] : '';

            const value = String(children).replace(/\n$/, '');

            // Display inline code with better styling
            if (inline) {
                return (
                    <code className={className} {...props}>
                        {children}
                    </code>
                );
            }

            // Remove any black redaction-style blocks from code
            const cleanedValue = value
                .replace(/█+/g, 'value') // Replace black blocks with "value"
                .replace(/⬛+/g, 'value'); // Replace black square blocks with "value"

            // Use syntax highlighting for code blocks
            return (
                <SyntaxHighlighter
                    style={vscDarkPlus}
                    language={language}
                    PreTag="div"
                    className="rounded-md"
                    customStyle={{
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        backgroundColor: 'rgba(28, 28, 48, 0.9)',
                        marginTop: '1rem',
                        marginBottom: '1rem'
                    }}
                >
                    {cleanedValue}
                </SyntaxHighlighter>
            );
        },
        // @ts-ignore - Ignoring type issues with ReactMarkdown components
        pre({ children }: any) {
            return <>{children}</>;
        },
        // Add proper styling for emphasized text that might be showing as redaction blocks
        // @ts-ignore - Ignoring type issues with ReactMarkdown components
        strong({ children }: any) {
            return <strong className="font-bold">{children}</strong>;
        },
        // Ensure list items display properly
        // @ts-ignore - Ignoring type issues with ReactMarkdown components
        li({ children, ...props }: any) {
            return (
                <li className="ml-4" {...props}>
                    {children}
                </li>
            );
        }
    };

    return (
        <div className="markdown-body prose dark:prose-invert max-w-none">
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={components}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
};

export default MarkdownRenderer; 